import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { SwiperProps, SwiperSlide } from 'swiper/react';
import { Lazy, Navigation } from 'swiper';

import CustomIcon from '@components/utils/CustomIcon';
import { RightArrowIcon } from '@assets/icons';
import FilterTabs from '@components/views/homepage/components/MostPreferredHotels/FilterTabs';
import { FlexibleDiv } from '@components/formStyles';
import {
  HotelViewModel,
  PreferredHotelCategoryViewModel,
  PreferredHotelItemViewModel,
  WishListItemViewModel,
  WishListViewModel,
} from '@api/v3';
import { useDevice } from '@contexts/device';
import Typography from '@components/Typography';
import IsInViewPort from '@components/ui/IsInViewPort';
import { productImpression } from '@utils/newDataLayer/productImpression';
import { hotelMostPreferred } from '@utils/newDataLayer/productMostPreferred';

import PreferredHotelsItem from './Item';
import {
  Wrapper,
  Label,
  ItemWrapper,
  FilterTbWrapper,
  SelectedUrl,
  ContentWrapper,
} from './styled';
import Carousel from './Carousel';

export interface Props {
  tabs: Array<PreferredHotelCategoryViewModel>;
  hotelItems?: Array<PreferredHotelItemViewModel>;
  swiperProps?: SwiperProps;
  content?: string;
  hotels?: HotelViewModel[];
  favoriteLists?: WishListViewModel[];
  refetchFavoriteLists?: any;
  defaultList?: WishListViewModel;
  defaultListItems?: WishListItemViewModel[];
  refetchDefaultList?: any;
  isRedirectingSet?: Dispatch<SetStateAction<boolean>>;
}
export interface TabProps {
  tab: PreferredHotelCategoryViewModel;
  hotelItems?: Array<PreferredHotelItemViewModel>;
  swiperProps?: SwiperProps;
  favoriteLists?: WishListViewModel[];
  refetchFavoriteLists?: any;
  defaultList?: WishListViewModel;
  defaultListItems?: WishListItemViewModel[];
  refetchDefaultList?: any;
  isRedirectingSet?: Dispatch<SetStateAction<boolean>>;
}
const SelectedTab = ({
  hotelItems,
  tab,
  swiperProps,
  favoriteLists,
  refetchFavoriteLists,
  defaultList,
  defaultListItems,
  refetchDefaultList,
  isRedirectingSet,
}: TabProps) => {
  const hotels = hotelItems?.filter((item) => {
    return (
      item?.categories?.[0]?.text?.[0]?.codename === tab?.text?.[0]?.codename
    );
  });

  return (
    <Carousel
      {...swiperProps}
      preloadImages={false}
      lazy={{
        enabled: true,
      }}
      modules={[Lazy, Navigation]}
    >
      {hotels?.map((hotelItem, index) => (
        <SwiperSlide key={hotelItem.name}>
          <IsInViewPort
            key={hotelItem.name}
            onViewVisible={() => {
              hotelMostPreferred(hotels, false);
            }}
          >
            <PreferredHotelsItem
              itemType={hotelItem.itemType}
              hotel={hotels[index]}
              index={index + 1}
              title={hotelItem?.name ?? ''}
              price={hotelItem.price ? hotelItem?.price : 0}
              image={{
                url: hotelItem?.images?.[0]?.url ?? '',
                alt: hotelItem?.images?.[0]?.alt,
              }}
              favoriteLists={favoriteLists}
              refetchFavoriteLists={refetchFavoriteLists}
              defaultList={defaultList}
              defaultListItems={defaultListItems}
              refetchDefaultList={refetchDefaultList}
              isRedirectingSet={isRedirectingSet}
            />
          </IsInViewPort>
        </SwiperSlide>
      ))}
    </Carousel>
  );
};
const MostPreferredHotels = ({
  tabs,
  hotelItems,
  swiperProps,
  content,
  favoriteLists,
  refetchFavoriteLists,
  defaultList,
  defaultListItems,
  refetchDefaultList,
  isRedirectingSet,
}: Props) => {
  const { isDesktop } = useDevice();
  const filteredTabItems = useMemo(() => {
    return tabs?.filter((tab) => {
      return hotelItems?.find((hotelItem) => {
        return (
          hotelItem?.categories?.[0]?.text?.[0]?.codename ===
          tab?.text?.[0]?.codename
        );
      });
    });
  }, [tabs, hotelItems]);
  const [selectedTabKey, setSelectedTabKey] = useState<string>(
    filteredTabItems?.[0]?.text?.[0]?.codename ?? ''
  );
  const [selectedLink, setSelectedLink] = useState('');
  const [selectedName, setSelectedName] = useState('');

  useEffect(() => {
    tabs?.map((item) => {
      if (item.text[0].codename === selectedTabKey) {
        setSelectedLink(item?.link?.href ?? '');
        setSelectedName(item?.link?.name ?? '');
      }
    });
  }, [selectedTabKey]);

  return (
    <Wrapper>
      <Label>En Çok Tercih Edilen Oteller</Label>
      {filteredTabItems?.length > 1 ? (
        <FlexibleDiv bottom={20}>
          <FilterTbWrapper>
            <FilterTabs
              tabs={tabs}
              defaultSelectedTabKey={
                filteredTabItems?.[0]?.text?.[0]?.codename ?? ''
              }
              onTabSelected={setSelectedTabKey}
              hotelItems={hotelItems}
            />
            {isDesktop ? (
              <SelectedUrl
                onClick={() => {
                  productImpression({
                    item_list_location: 'Anasayfa En Çok Tercih Edilen Oteller',
                    item_list_id: selectedName.includes('Oteller')
                      ? selectedName
                      : `${selectedName} Otelleri`,
                    type: 'hotel',
                  });
                  isRedirectingSet?.(true);
                  window.open(selectedLink, '_self');
                }}
              >
                {selectedName}{' '}
                <CustomIcon fontSize={24} icon={RightArrowIcon} />
              </SelectedUrl>
            ) : null}
          </FilterTbWrapper>
        </FlexibleDiv>
      ) : null}
      {tabs?.map((tab, tabIndex) =>
        selectedTabKey === tab.text[0].codename ? (
          <div key={tabIndex}>
            <ItemWrapper key={tabIndex}>
              <SelectedTab
                tab={tab}
                hotelItems={hotelItems}
                swiperProps={swiperProps}
                favoriteLists={favoriteLists}
                refetchFavoriteLists={refetchFavoriteLists}
                defaultList={defaultList}
                defaultListItems={defaultListItems}
                refetchDefaultList={refetchDefaultList}
                isRedirectingSet={isRedirectingSet}
              />
            </ItemWrapper>
            {!isDesktop ? (
              <SelectedUrl
                onClick={() => {
                  productImpression({
                    item_list_location: 'Anasayfa En Çok Tercih Edilen Oteller',
                    item_list_id: selectedName.includes('Oteller')
                      ? selectedName
                      : `${selectedName} Otelleri`,
                    type: 'hotel',
                  });
                  isRedirectingSet?.(true);
                  window.open(selectedLink, '_self');
                }}
              >
                {selectedName}{' '}
                <CustomIcon fontSize={24} icon={RightArrowIcon} />
              </SelectedUrl>
            ) : null}
          </div>
        ) : null
      )}
      {content ? (
        <ContentWrapper>
          <FlexibleDiv left={isDesktop ? 0 : 15}>
            <Typography variant='caption2' color='D100' weight='Regular'>
              {content}
            </Typography>
          </FlexibleDiv>
        </ContentWrapper>
      ) : (
        ''
      )}
    </Wrapper>
  );
};

export default MostPreferredHotels;
